import { useEffect, useRef, useCallback, useState } from 'react';
import { GpuAgent } from '../services/api';

type AgentUpdateMessage = {
  type: 'agent:update';
  agent: GpuAgent;
};

type AgentOfflineMessage = {
  type: 'agent:offline';
  agentId: string;
};

type RentalExpiredMessage = {
  type: 'metering:rental_expired';
  rentalId: string;
  reason: string;
};

type RentalReleasedMessage = {
  type: 'metering:rental_released';
  rentalId: string;
  listingId: string;
  reason: string;
};

type WsMessage = AgentUpdateMessage | AgentOfflineMessage | RentalExpiredMessage | RentalReleasedMessage | { type: string };

interface UseAgentSyncOptions {
  onAgentUpdate?: (agent: GpuAgent) => void;
  onAgentOffline?: (agentId: string) => void;
  onRentalExpired?: (rentalId: string, reason: string) => void;
  onRentalReleased?: (rentalId: string, listingId: string, reason: string) => void;
  enabled?: boolean;
}

const MAX_RECONNECT_DELAY = 30_000;
const BASE_RECONNECT_DELAY = 1_000;

export function useAgentSync({ onAgentUpdate, onAgentOffline, onRentalExpired, onRentalReleased, enabled = true }: UseAgentSyncOptions) {
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const timersRef = useRef<{
    reconnect: ReturnType<typeof setTimeout> | null;
    connect: ReturnType<typeof setTimeout> | null;
  }>({ reconnect: null, connect: null });
  const attemptRef = useRef(0);
  const callbacksRef = useRef({ onAgentUpdate, onAgentOffline, onRentalExpired, onRentalReleased });

  callbacksRef.current = { onAgentUpdate, onAgentOffline, onRentalExpired, onRentalReleased };

  const abandonSocket = useCallback((ws: WebSocket) => {
    ws.onopen = null;
    ws.onclose = null;
    ws.onerror = null;
    ws.onmessage = null;
    // Only close sockets that have completed the handshake; calling
    // close() on a CONNECTING socket triggers Chrome's spurious
    // "WebSocket is closed before the connection is established" error.
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CLOSING) {
      ws.close();
    } else if (ws.readyState === WebSocket.CONNECTING) {
      // Let the handshake finish, then close immediately.
      // Server-side auth timeout (10 s) guarantees cleanup if onopen never fires.
      const abandon = ws;
      abandon.onopen = () => abandon.close();
    }
  }, []);

  const cleanup = useCallback(() => {
    if (timersRef.current.connect) {
      clearTimeout(timersRef.current.connect);
      timersRef.current.connect = null;
    }
    if (timersRef.current.reconnect) {
      clearTimeout(timersRef.current.reconnect);
      timersRef.current.reconnect = null;
    }
    if (wsRef.current) {
      abandonSocket(wsRef.current);
      wsRef.current = null;
    }
    setIsConnected(false);
  }, [abandonSocket]);

  const openSocket = useCallback(() => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${window.location.host}/ws`);
    wsRef.current = ws;

    ws.onopen = () => {
      // Server authenticates from the httpOnly session cookie on upgrade.
    };

    ws.onmessage = (event) => {
      try {
        const msg: WsMessage = JSON.parse(event.data);

        if (msg.type === 'auth:ok') {
          setIsConnected(true);
          attemptRef.current = 0;
          return;
        }

        if (msg.type === 'auth:error') {
          ws.close();
          return;
        }

        if (msg.type === 'agent:update') {
          const { agent } = msg as AgentUpdateMessage;
          callbacksRef.current.onAgentUpdate?.(agent);
        }

        if (msg.type === 'agent:offline') {
          const { agentId } = msg as AgentOfflineMessage;
          callbacksRef.current.onAgentOffline?.(agentId);
        }

        if (msg.type === 'metering:rental_expired') {
          const { rentalId, reason } = msg as RentalExpiredMessage;
          callbacksRef.current.onRentalExpired?.(rentalId, reason);
        }

        if (msg.type === 'metering:rental_released') {
          const { rentalId, listingId, reason } = msg as RentalReleasedMessage;
          callbacksRef.current.onRentalReleased?.(rentalId, listingId, reason);
        }
      } catch {
        // ignore malformed messages
      }
    };

    ws.onclose = () => {
      setIsConnected(false);
      if (wsRef.current === ws) wsRef.current = null;
      const delay = Math.min(BASE_RECONNECT_DELAY * Math.pow(2, attemptRef.current), MAX_RECONNECT_DELAY);
      attemptRef.current++;
      timersRef.current.reconnect = setTimeout(() => {
        timersRef.current.reconnect = null;
        connect();
      }, delay);
    };

    ws.onerror = () => {
      // onclose fires after this
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = useCallback(() => {
    cleanup();
    timersRef.current.connect = setTimeout(() => {
      timersRef.current.connect = null;
      openSocket();
    }, 0);
  }, [cleanup, openSocket]);

  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      cleanup();
    }
    return cleanup;
  }, [enabled, connect, cleanup]);

  return { isConnected };
}
